import { action, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { isNumber } from 'lodash';
import * as React from 'react';

import ActionableDivider from '../actionable-divider';
import EdittableCard from './EdittableCard';
import InputField from '../forms/input-field';
import JobHistoryEditorStore from '../../stores/JobHistoryEditorStore';
import Modal from '../modal';
import request from '../utils/Request';
import Select from '../forms/select';

import { JobHistory, JobHistoryEditorProps } from '../../types';

@inject('jobHistoryEditorStore')
@observer
class JobHistoryEditor extends React.Component<JobHistoryEditorProps> {
  private store: JobHistoryEditorStore;

  public constructor(props: JobHistoryEditorProps) {
    super(props);
    this.store = props.jobHistoryEditorStore!;
  }

  public render() {
    return (
      <>
        {/* render the blocks first */}
        {this.createJobHistoryModal()}

        {/* render the blocks first */}
        <div className="columns is-multiline edittable-card-container is-marginless">
          {this.renderJobHistories()}
        </div>

        {/* allow adding new blocks for the form */}
        <ActionableDivider
            action={this.showModal}
            actionBoxClassName="has-background-primary"
            className="column is-12"
        >
          <span className="icon has-text-white"><i className="material-icons is-size-4">add</i></span>
        </ActionableDivider>
      </>
    );
  }

  private renderJobHistories = () => {
    const blocks = this.store.jobs.map((job, blockId) => {
      return (
        <EdittableCard
            blockId={blockId}
            key={`job-${blockId}`}
            onDeleteClick={this.handleDeleteClick}
            onEditClick={this.handleEditClick}
        >
          <p className="title is-size-6">{job.title}</p>
          <p className="subtitle is-size-6">{job.level}</p>
          <p className="is-size-6">{`${job.category}, ${job.years} years`}</p>
        </EdittableCard>
      );
    });

    // Indicates to user that the list is empty
    const emptyPrompt =
        <div className="column is-12 has-text-centered has-text-grey">
          <div className="empty-block-prompt-icon"><i className="fas fa-briefcase" /></div>
          <p className="is-size-5 has-text-weight-bold">Now, tell us about your work experiences</p>
          <p className="is-size-6">This helps us find people who know about your industry.</p>
        </div>;

    return blocks.length > 0 ? blocks : emptyPrompt;
  }

  private createJobHistoryModal = () => {
    const yearsString = !this.store.years ?  '' : `${this.store.years}`;

    return (
      <Modal
          isActive={this.store.isEditModalActive}
      >
        <h3 className="title is-size-5">Add job history</h3>
        <div className="columns">
          <InputField
              label="Job Title"
              layoutClassName="column is-12"
              helpMessage={this.store.titleHelpMessage}
              highlight={this.store.titleHighlight}
              name="title"
              onChange={this.handleTitleChange}
              placeholder="Software Engineer"
              type="text"
              value={this.store.title}
          />
        </div>
        <div className="columns">
          <Select
              label="Job Level"
              layoutClassName="column is-6"
              helpMessage={this.store.levelHelpMessage}
              highlight={this.store.levelHighlight}
              onChange={this.handleLevelChange}
              options={[ "Intern", "Junior", "Intermediate", "Senior", "Principal" ]}
              value={this.store.level}
          />
          <InputField
              label="# of Years"
              layoutClassName="column is-6"
              helpMessage={this.store.yearsHelpMessage}
              highlight={this.store.yearsHighlight}
              name="years"
              onChange={this.handleYearsChange}
              placeholder="5"
              type="number"
              value={yearsString}
          />
        </div>
        <div className="columns">
          <InputField
              label="Job Category"
              layoutClassName="column is-12"
              helpMessage={this.store.categoryHelpMessage}
              highlight={this.store.categoryHighlight}
              name="categories"
              onChange={this.handleCategoryChange}
              placeholder="Sales, Engineering, Marketing, etc."
              type="text"
              value={this.store.category}
          />
        </div>

        <div className="columns modal-buttons">
          <div className="column">
            <button
                className="button"
                disabled={!!this.store.awaitServer}
                onClick={this.closeModal}
            >
              Cancel
            </button>
          </div>
          <div className="column">
            <button
                className={`button is-primary ${this.store.awaitServer && 'is-loading'}`}
                disabled={!!this.store.awaitServer}
                onClick={this.handleSaveClick}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  @action
  private handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.store.category = event.currentTarget.value;
    this.store.categoryBlurred = true;
  }

  @action
  private handleLevelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.store.level = event.currentTarget.value;
    this.store.levelBlurred = true;
  }

  @action
  private handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.store.title = event.currentTarget.value;
    this.store.titleBlurred = true;
  }

  @action
  private handleYearsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.store.years = +event.currentTarget.value;
    this.store.yearsBlurred = true;
  }

  private handleEditClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    const blockId = event.currentTarget.attributes.getNamedItem('data-block-id');
    if (blockId) {
      this.showModal(+blockId.value);
    }
  }

  @action
  private handleDeleteClick = async (event: React.MouseEvent<HTMLSpanElement>) => {
    const blockId = event.currentTarget.attributes.getNamedItem('data-block-id');
    if (blockId) {
      const jobs = this.store.jobs;
      const deleted = jobs.splice(+blockId.value, 1);
      
      try {
        await request.delete(`/jobs/${deleted[0].id}`);
        runInAction(() => this.store.jobs = jobs);
      } catch (err) {
        alert('Unable to delete; please try again later.');
        console.error(err);
      }
    }
  }

  @action
  private handleSaveClick = async () => {
    if (this.store.checkReadyForSave()) {
      const { edittingBlockId, category, level, title, years } = this.store;

      const jobHistories: JobHistory[] = this.store.jobs;
      const isUpdate = jobHistories.length > edittingBlockId;
      const oldJobHistory = jobHistories[edittingBlockId] || {};
      const newJobHistory =  { ...oldJobHistory, category, level, title, years };
      jobHistories[edittingBlockId] = newJobHistory;
      
      const jsonData = { category, level, role: title, years}
      this.store.awaitServer = true;
      const requestPromise = isUpdate
          ? request.put(`/jobs/${newJobHistory.id}/`, jsonData)
          : request.post(`/jobs/`, jsonData);

      const response = await requestPromise;
      runInAction(() => {
        if (response.data.id) {
          jobHistories[edittingBlockId].id = response.data.id;
        }
        this.store.awaitServer = false;
      });

      this.closeModal();
    }
  }

  /**
   * Shows the edit modal.
   *
   * @param blockId The block we are edditing; if not supplied, assumes we are adding a new education block.
   */
  @action
  private showModal = (blockId?: number) => {
    const hasBlockId = isNumber(blockId);
    const existingJobHistories = this.store.jobs;

    this.store.edittingBlockId = hasBlockId ? blockId! : existingJobHistories.length;
    if (existingJobHistories.length > this.store.edittingBlockId) {
      const oldJobHistory = existingJobHistories[this.store.edittingBlockId];
      this.store.category = oldJobHistory.category;
      this.store.level = oldJobHistory.level;
      this.store.title = oldJobHistory.title;
      this.store.years = oldJobHistory.years;
    }

    this.store.isEditModalActive = true;
  }

  /**
   * Clear the editor modal and close it.
   */
  @action
  private closeModal = () => {
    this.store.category = '';
    this.store.categoryBlurred = false;
    this.store.level = '';
    this.store.levelBlurred = false;
    this.store.title = '';
    this.store.titleBlurred = false
    this.store.years = 0;
    this.store.yearsBlurred = false;
    this.store.isEditModalActive = false;
  }
}

export default JobHistoryEditor;



// WEBPACK FOOTER //
// ./src/common/widgets/JobHistoryEditor.tsx